import { adminClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  role?: string | null;
}

export interface SessionState {
  data: { user: SessionUser } | null;
  isPending: boolean;
}

// Kept module-private on purpose: better-auth's inferred client type references
// zod internals via a non-portable pnpm path, which breaks declaration emit
// (TS2742) if exported under `composite`. The app consumes the thin,
// explicitly-typed wrappers below instead.
const client = createAuthClient({
  baseURL: typeof window !== 'undefined' ? window.location.origin : undefined,
  plugins: [adminClient()],
});

/** React hook: current session (`data.user` or null) + `isPending`. */
export const useSession = client.useSession as unknown as () => SessionState;

/** Email/password sign-in. Resolves with `{ error }` on bad credentials. */
export const signInWithEmail = (
  email: string,
  password: string,
): Promise<{ error?: { message?: string } | null }> => client.signIn.email({ email, password });

export const signOut = (): Promise<unknown> => client.signOut();

/** Send a password-reset email (always resolves — no account-existence probing). */
export const requestPasswordReset = (email: string): Promise<unknown> =>
  client.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });

/** Complete a password reset with the emailed token. */
export const resetPassword = (
  newPassword: string,
  token: string,
): Promise<{ error?: { message?: string } | null }> => client.resetPassword({ newPassword, token });
